import { useMemo } from 'react';
import type { OrderDetail } from '@/api/orders';
import { fmtDate } from '../lib';
import { taskLabel } from '../workUtils';

interface NoteEntry {
  key: string;
  label: string;
  text: string;
}

/**
 * Everything the run wrote about this journey, verbatim, in one place — the
 * tab is the request. Nothing here renders anywhere else on the journey.
 */
export function NotesTab({ detail }: { detail: OrderDetail }) {
  const entries = useMemo<NoteEntry[]>(() => {
    const out: NoteEntry[] = [];
    const add = (key: string, label: string, text: string | null | undefined) => {
      if (text && text.trim()) out.push({ key, label, text });
    };
    add('journey', 'Journey', detail.notes);
    add('completeness', `Ordered document${detail.orderedCompleteness ? ` · ${detail.orderedCompleteness}` : ''}`, detail.orderedCompletenessNote);
    for (const a of detail.announcements) {
      add(`ann-${a.id}`, `Announcement${a.announcedDate ? ` · ${fmtDate(a.announcedDate)}` : ''}`, a.notes);
    }
    for (const inv of detail.invoices) {
      const name = `Invoice ${inv.invoiceNo || '(no number)'}`;
      add(`inv-${inv.id}`, name, inv.notes);
      add(`inv-conflict-${inv.id}`, `${name} · sources disagree`, inv.sourceConflictNote);
    }
    for (const r of detail.receipts) {
      add(`rcpt-bal-${r.id}`, `Receiving #${r.receivingId} · balanced`, r.balancedNote);
    }
    for (const doc of detail.documents) {
      add(`doc-note-${doc.id}`, doc.filename, doc.note);
      add(`doc-outcome-${doc.id}`, `${doc.filename} · processed`, doc.outcome);
    }
    for (const t of detail.tasks) {
      add(`task-note-${t.id}`, `${taskLabel(t.kind)} · ${fmtDate(t.createdAt)}`, t.note);
      add(`task-outcome-${t.id}`, `${taskLabel(t.kind)} · outcome`, t.outcome);
    }
    return out;
  }, [detail]);

  if (entries.length === 0) {
    return <p className="flex flex-col items-center gap-3 px-6 py-12 text-center text-[14px] text-muted-foreground">No notes recorded on this journey.</p>;
  }
  return (
    <dl className="divide-y divide-border/40">
      {entries.map((e) => (
        <div key={e.key} className="grid gap-1 py-3 md:grid-cols-[14rem_minmax(0,1fr)] md:gap-4">
          <dt className="text-xs text-muted-foreground">{e.label}</dt>
          <dd className="whitespace-pre-wrap text-sm">{e.text}</dd>
        </div>
      ))}
    </dl>
  );
}
